import { useMemo, useState } from "react";
import {
	Box,
	Button,
	ButtonGroup,
	Flex,
	HStack,
	Heading,
	SimpleGrid,
	Spinner,
	Stat,
	StatLabel,
	StatNumber,
	Text,
	VStack,
	Wrap,
	WrapItem,
	Select,
	useBreakpointValue,
} from "@chakra-ui/react";
import { format, startOfMonth, subMonths, subYears } from "date-fns";
import { es as esLocale, enUS as enLocale } from "date-fns/locale";

import { TemplatePage } from "components/layout/template";
import { useLangContext } from "configs/languageContext";
import { useDataContext } from "configs/dataContext";
import { dashboard, translation } from "data/translations";
import { HeatmapMap } from "./components/heatmapMap";

type RangeKey = "3m" | "6m" | "12m" | "24m" | "5y" | "all";
const RANGE_ORDER: RangeKey[] = ["3m", "6m", "12m", "24m", "5y", "all"];

function rangeStart(key: RangeKey, now: Date): Date | null {
	switch (key) {
		case "3m":  return startOfMonth(subMonths(now, 2));
		case "6m":  return startOfMonth(subMonths(now, 5));
		case "12m": return startOfMonth(subMonths(now, 11));
		case "24m": return startOfMonth(subMonths(now, 23));
		case "5y":  return startOfMonth(subYears(now, 5));
		case "all": return null;
	}
}

function toDate(ts: any): Date | null {
	if (!ts) return null;
	if (ts instanceof Date) return ts;
	if (typeof ts.toDate === "function") return ts.toDate();
	const seconds = ts.seconds ?? ts._seconds;
	if (typeof seconds === "number") return new Date(seconds * 1000);
	return null;
}

function parseLatLng(loc: any): [number, number] | null {
	if (!loc) return null;
	if (Array.isArray(loc) && loc.length >= 2) {
		const lat = typeof loc[0] === "string" ? parseFloat(loc[0]) : loc[0];
		const lng = typeof loc[1] === "string" ? parseFloat(loc[1]) : loc[1];
		if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
		return [lat, lng];
	}
	if (typeof loc === "object" && "latitude" in loc && "longitude" in loc) {
		return [Number(loc.latitude), Number(loc.longitude)];
	}
	return null;
}

const EmployeeMapPage = () => {
	const { lang } = useLangContext();
	const { data, loading } = useDataContext();
	const dateLocale = lang === "es" ? esLocale : enLocale;

	const [range, setRange] = useState<RangeKey>("all");
	const [typeFilter, setTypeFilter] = useState<string>("all");

	const t = (key: string) => dashboard[key]?.[lang] ?? key;
	const labelType = (k: string) => dashboard[`type_${k}`]?.[lang] ?? k;

	const enriched = useMemo(
		() => (data || []).map((a: any) => ({
			...a,
			_date: toDate(a.accident_timestamp),
			_latlng: parseLatLng(a.accident_location),
		})),
		[data]
	);

	const now = useMemo(() => new Date(), []);
	const rangeStartDate = useMemo(() => rangeStart(range, now), [range, now]);

	const typeOptions = useMemo(() => {
		const s = new Set<string>();
		enriched.forEach((a: any) => a.accident_type && s.add(a.accident_type));
		return Array.from(s).sort();
	}, [enriched]);

	const filtered = useMemo(() => {
		return enriched.filter((a: any) => {
			if (rangeStartDate && (!a._date || a._date < rangeStartDate)) return false;
			if (typeFilter !== "all" && a.accident_type !== typeFilter) return false;
			return true;
		});
	}, [enriched, rangeStartDate, typeFilter]);

	const withCoords = useMemo(
		() => filtered.filter((a: any) => a._latlng),
		[filtered]
	);

	const noCoords = filtered.length - withCoords.length;

	const heatPoints = useMemo<[number, number, number?][]>(
		() => withCoords.map((a: any) => [a._latlng[0], a._latlng[1], 0.6] as [number, number, number]),
		[withCoords]
	);

	// Responsive map height — single map instance avoids Leaflet duplicate-init bug.
	const mapHeight = useBreakpointValue({ base: 420, md: 620 }) ?? 620;

	if (loading && (data?.length ?? 0) === 0) {
		return (
			<TemplatePage sidebar>
				<Box p={6}>
					<Flex justify="center" align="center" py={20}>
						<Spinner size="xl" mr={4} />
						<Text>{t("loading_reports")}</Text>
					</Flex>
				</Box>
			</TemplatePage>
		);
	}

	return (
		<TemplatePage sidebar>
			<VStack align="stretch" spacing={5} w="100%">
				{/* Header */}
				<Box>
					<Heading size="lg" mb={1}>
						{translation.emp_selector_map[lang]}
					</Heading>
					<Text color="gray.500" fontSize="sm">
						{t("chart_heatmap_sub")}
					</Text>
				</Box>

				{/* Filter row */}
				<Wrap spacing={4} align="center">
					<WrapItem>
						<HStack>
							<Text fontSize="sm" color="gray.600" fontWeight="medium">
								{t("range_label")}:
							</Text>
							<ButtonGroup size="sm" isAttached variant="outline">
								{RANGE_ORDER.map((k) => (
									<Button
										key={k}
										onClick={() => setRange(k)}
										colorScheme={range === k ? "blue" : "gray"}
										variant={range === k ? "solid" : "outline"}
									>
										{t(`range_${k}`)}
									</Button>
								))}
							</ButtonGroup>
						</HStack>
					</WrapItem>
					<WrapItem>
						<HStack>
							<Text fontSize="sm" color="gray.600" fontWeight="medium">
								{t("filter_type")}:
							</Text>
							<Select
								size="sm"
								value={typeFilter}
								onChange={(e) => setTypeFilter(e.target.value)}
								minW="200px"
								bg="white"
							>
								<option value="all">{t("filter_all")}</option>
								{typeOptions.map((tk) => (
									<option key={tk} value={tk}>
										{labelType(tk)}
									</option>
								))}
							</Select>
						</HStack>
					</WrapItem>
				</Wrap>

				{/* Stats strip — grid on mobile, dividers on desktop */}
				<Box
					bg="white"
					borderWidth="1px"
					borderColor="gray.200"
					rounded="xl"
					p={{ base: 3, md: 4 }}
					boxShadow="sm"
				>
					<SimpleGrid columns={{ base: 2, md: 4 }} spacing={{ base: 3, md: 6 }}>
						<Stat>
							<StatLabel fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
								{t("map_total_in_range")}
							</StatLabel>
							<StatNumber fontSize={{ base: "xl", md: "2xl" }} color="blue.600">{filtered.length.toLocaleString()}</StatNumber>
						</Stat>
						<Stat>
							<StatLabel fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
								{t("map_with_coords")}
							</StatLabel>
							<StatNumber fontSize={{ base: "xl", md: "2xl" }} color="green.600">{withCoords.length.toLocaleString()}</StatNumber>
						</Stat>
						<Stat>
							<StatLabel fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
								{t("map_no_coords_label")}
							</StatLabel>
							<StatNumber fontSize={{ base: "xl", md: "2xl" }} color="orange.500">{noCoords.toLocaleString()}</StatNumber>
						</Stat>
						{rangeStartDate && (
							<Stat>
								<StatLabel fontSize="xs" color="gray.500" textTransform="uppercase" letterSpacing="wide">
									{t("map_since")}
								</StatLabel>
								<StatNumber fontSize={{ base: "sm", md: "md" }} color="gray.700">
									{format(rangeStartDate, "PP", { locale: dateLocale })}
								</StatNumber>
							</Stat>
						)}
					</SimpleGrid>
				</Box>

				{/* The map — single instance, responsive height */}
				<Box
					bg="white"
					borderWidth="1px"
					borderColor="gray.200"
					rounded="xl"
					p={{ base: 2, md: 3 }}
					boxShadow="sm"
				>
					{heatPoints.length === 0 ? (
						<Text color="gray.500" py={20} textAlign="center">{t("map_no_coords")}</Text>
					) : (
						<HeatmapMap points={heatPoints} height={mapHeight} />
					)}
				</Box>
			</VStack>
		</TemplatePage>
	);
};

export default EmployeeMapPage;
